<template>
  <v-progress-circular
    v-if="isLoading"
    :size="70"
    :width="7"
    color="primary"
    indeterminate
    class="centered"
  ></v-progress-circular>
  <div v-else>
    <h1 class="text-center mt-3">Referral Campaign</h1>
    <div class="form shadow p-5 mb-5 bg-white rounded">
      <!-- Campaign Information Section -->
      <div class="information-group pb-5 mb-5">
        <div class="row information-title-wrapper mx-0 my-0 px-2">
          <div class="information-title">Campaign Information</div>
        </div>
        <div class="px-6 py-3">
          <!-- Row 1: Brand + Status -->
          <div class="row mb-3">
            <div class="col-6">
              <BrandDropdown
                v-model="form.brandSelected"
                @setBrand="setBrand"
                @setBrands="setBrands"
                required="true"
                :setDisable="!!id"
              ></BrandDropdown>
              <div v-if="isSubmit && error.errorBrandCode" class="text-error">
                {{ error.errorBrandCode.message }}
              </div>
            </div>
            <div class="col-6">
              <label for="checkStatus">Status</label>
              <div class="mt-1">
                <input class="form-check-input mr-2" type="checkbox" id="checkStatus" v-model="form.status" />
                <label class="form-check-label" for="checkStatus">Active</label>
              </div>
              <div v-if="isSubmit && error.errorStatus" class="text-error">
                {{ error.errorStatus.message }}
              </div>
            </div>
          </div>
          <!-- Row 2: Start Date + End Date -->
          <div class="row mb-3">
            <div class="col-6">
              <label for="startDatePicker" required>Start Date</label>
              <div class="d-flex align-items-center gap-1">
                <date-picker
                  id="startDatePicker"
                  v-bind="$attrs"
                  v-model="form.startDate"
                  format="D MMMM yyyy"
                  lang="en"
                  :value="form.startDate"
                  @change="changeStartDate($event); form.startDate = $event;"
                  style="margin-top: -0.6rem"
                  :class="{ 'input--error': isSubmit && error.errorStartDate }"
                />
                <button class="btn btn-outline-secondary btn-clear" @click="() => (form.startDate = null)">Clear</button>
              </div>
              <div v-if="isSubmit && error.errorStartDate" class="text-error">
                {{ error.errorStartDate.message }}
              </div>
            </div>
            <div class="col-6">
              <label for="endDatePicker" required>End Date</label>
              <div class="d-flex align-items-center gap-1">
                <date-picker
                  id="endDatePicker"
                  v-bind="$attrs"
                  v-model="form.endDate"
                  format="D MMMM yyyy"
                  lang="en"
                  :value="form.endDate"
                  :minDate="form.startDate"
                  @change="form.endDate = $event"
                  style="margin-top: -0.6rem"
                  :class="{ 'input--error': isSubmit && error.errorEndDate }"
                />
                <button class="btn btn-outline-secondary btn-clear" @click="() => (form.endDate = null)">Clear</button>
              </div>
              <div v-if="isSubmit && error.errorEndDate" class="text-error">
                {{ error.errorEndDate.message }}
              </div>
            </div>
          </div>
          <!-- Row 3: Campaign Banner -->
          <div class="row mb-3">
            <div class="col">
              <label for="uploadBannerImage">Campaign Banner</label>
              <div>
                <input
                  type="file"
                  id="uploadBannerImage"
                  ref="uploadBannerImage"
                  accept="image/*"
                  @change="uploadBannerImage"
                />
                <FileLoading v-if="isFileLoading" />
              </div>
              <div v-if="form.bannerPreviewUrl" class="mt-2">
                <img :src="form.bannerPreviewUrl" alt="Campaign Banner" class="banner-preview" />
                <div class="mt-1">
                  <button class="btn btn-outline-danger btn-sm" @click="deleteBannerImage">Remove</button>
                </div>
              </div>
              <div v-if="isSubmit && error.errorCampaignBanner" class="text-error">
                {{ error.errorCampaignBanner.message }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Localized Content Section -->
      <div class="information-group pb-5 mb-5">
        <div class="row information-title-wrapper mx-0 my-0 px-2">
          <div class="information-title d-flex align-items-center">
            Localized Content
            <div class="lang-toggle ml-3">
              <button
                type="button"
                class="lang-toggle__btn"
                :class="{ 'lang-toggle__btn--active': selectedLang === 'EN' }"
                @click="selectedLang = 'EN'"
              >
                EN
              </button>
              <button
                type="button"
                class="lang-toggle__btn"
                :class="{ 'lang-toggle__btn--active': selectedLang === 'TH' }"
                @click="selectedLang = 'TH'"
              >
                TH
              </button>
            </div>
          </div>
        </div>
        <div class="px-6 py-3">
          <div class="row mb-3">
            <div class="col-6">
              <label for="campaignName" required>Campaign Name ({{ selectedLang }})</label>
              <input
                id="campaignName"
                v-if="selectedLang === 'EN'"
                v-model="form.campaignNameEn"
                type="text"
                class="form-control"
                :class="{ 'input--error': isSubmit && error.errorCampaignNameEn }"
              />
              <input
                id="campaignName"
                v-else
                v-model="form.campaignNameTh"
                type="text"
                class="form-control"
                :class="{ 'input--error': isSubmit && error.errorCampaignNameTh }"
              />
              <div v-if="isSubmit && selectedLang === 'EN' && error.errorCampaignNameEn" class="text-error">
                {{ error.errorCampaignNameEn.message }}
              </div>
              <div v-if="isSubmit && selectedLang === 'TH' && error.errorCampaignNameTh" class="text-error">
                {{ error.errorCampaignNameTh.message }}
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col">
              <label for="campaignDetail">Campaign Detail ({{ selectedLang }})</label>
              <CkEditor
                v-if="selectedLang === 'EN'"
                :value="form.campaignDetailEn"
                :isTh="false"
                @setContent="setCampaignDetailEn"
              ></CkEditor>
              <CkEditor
                v-else
                :value="form.campaignDetailTh"
                :isTh="true"
                @setContent="setCampaignDetailTh"
              ></CkEditor>
              <div v-if="isSubmit && selectedLang === 'EN' && error.errorCampaignDetailEn" class="text-error">
                {{ error.errorCampaignDetailEn.message }}
              </div>
              <div v-if="isSubmit && selectedLang === 'TH' && error.errorCampaignDetailTh" class="text-error">
                {{ error.errorCampaignDetailTh.message }}
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col">
              <label for="termsAndConditions">Terms & Conditions ({{ selectedLang }})</label>
              <CkEditor
                v-if="selectedLang === 'EN'"
                :value="form.termsAndConditionsEn"
                :isTh="false"
                @setContent="setTermsAndConditionsEn"
              ></CkEditor>
              <CkEditor
                v-else
                :value="form.termsAndConditionsTh"
                :isTh="true"
                @setContent="setTermsAndConditionsTh"
              ></CkEditor>
              <div v-if="isSubmit && selectedLang === 'EN' && error.errorTermsAndConditionsEn" class="text-error">
                {{ error.errorTermsAndConditionsEn.message }}
              </div>
              <div v-if="isSubmit && selectedLang === 'TH' && error.errorTermsAndConditionsTh" class="text-error">
                {{ error.errorTermsAndConditionsTh.message }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Campaign Settings Section -->
      <div class="information-group pb-5 mb-5">
        <div class="row information-title-wrapper mx-0 my-0 px-2">
          <div class="information-title">Campaign Settings</div>
        </div>
        <div class="px-6 py-3">
          <div class="row">
            <div class="col-4">
              <label for="purchaseWindowDays" required>Purchase Within Days</label>
              <NumberInput
                v-model="form.purchaseWindowDays"
                input-id="purchaseWindowDays"
                :dynamic-class="{ 'input--error': isSubmit && error.errorPurchaseWindowDays }"
              />
              <div v-if="isSubmit && error.errorPurchaseWindowDays" class="text-error">
                {{ error.errorPurchaseWindowDays.message }}
              </div>
            </div>
            <div class="col-4">
              <label for="minimumNetAmount" required>Min Spend Amount</label>
              <NumberInput
                v-model="form.minimumNetAmount"
                input-id="minimumNetAmount"
                :is-decimal-allowed="true"
                :dynamic-class="{ 'input--error': isSubmit && error.errorMinimumNetAmount }"
              />
              <div v-if="isSubmit && error.errorMinimumNetAmount" class="text-error">
                {{ error.errorMinimumNetAmount.message }}
              </div>
            </div>
            <div class="col-4">
              <label for="maxSuccessfulRefereesPerCustomer" required>Max Success Referee Per Customer</label>
              <NumberInput
                v-model="form.maxSuccessfulRefereesPerCustomer"
                input-id="maxSuccessfulRefereesPerCustomer"
                :dynamic-class="{ 'input--error': isSubmit && error.errorMaxSuccessfulRefereesPerCustomer }"
              />
              <div v-if="isSubmit && error.errorMaxSuccessfulRefereesPerCustomer" class="text-error">
                {{ error.errorMaxSuccessfulRefereesPerCustomer.message }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Rewards & Privileges Section -->
      <div class="information-group pb-5 mb-5">
        <div class="row information-title-wrapper mx-0 my-0 px-2">
          <div class="information-title">Rewards & Privileges</div>
        </div>
        <div class="px-6 py-3">
          <div class="row">
            <div class="col-6">
              <label for="referrerPrivilege" required>Referrer Privilege</label>
              <multiselect
                id="referrerPrivilege"
                v-model="form.referrerRewardPrivilegeId"
                :options="privilegeOptions"
                :custom-label="(opt) => `(ID:${opt.id}) ${opt.text}`"
                label="text"
                track-by="id"
                placeholder="Select privilege"
                :disabled="!form.brandSelected"
              ></multiselect>
              <div v-if="isSubmit && error.errorReferrerRewardPrivilegeId" class="text-error">
                {{ error.errorReferrerRewardPrivilegeId.message }}
              </div>
            </div>
            <div class="col-6">
              <label for="refereePrivilege" required>Referee Privilege</label>
              <multiselect
                id="refereePrivilege"
                v-model="form.refereeRewardPrivilegeId"
                :options="privilegeOptions"
                :custom-label="(opt) => `(ID:${opt.id}) ${opt.text}`"
                label="text"
                track-by="id"
                placeholder="Select privilege"
                :disabled="!form.brandSelected"
              ></multiselect>
              <div v-if="isSubmit && error.errorRefereeRewardPrivilegeId" class="text-error">
                {{ error.errorRefereeRewardPrivilegeId.message }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Notification Templates Section -->
      <div class="information-group pb-5 mb-5">
        <div class="row information-title-wrapper mx-0 my-0 px-2">
          <div class="information-title">Notification Templates</div>
        </div>
        <div class="px-6 py-3">
          <div class="row mb-3">
            <div class="col">
              <label for="notificationReferee">
                Notification to Referee when Accept Invitation
                <font-awesome-icon :icon="['fas', 'info-circle']" id="tooltipReferee" />
              </label>
              <b-tooltip target="tooltipReferee" triggers="hover" placement="right">
                {{ tooltipText }}
              </b-tooltip>
              <textarea
                id="notificationReferee"
                v-model="form.notificationMessageTemplateRefereeOnAccept"
                class="form-control"
                rows="3"
                :class="{ 'input--error': isSubmit && error.errorNotificationMessageTemplateRefereeOnAccept }"
              ></textarea>
              <div v-if="isSubmit && error.errorNotificationMessageTemplateRefereeOnAccept" class="text-error">
                {{ error.errorNotificationMessageTemplateRefereeOnAccept.message }}
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col">
              <label for="notificationReferrer">
                Notification to Referrer
                <font-awesome-icon :icon="['fas', 'info-circle']" id="tooltipReferrer" />
              </label>
              <b-tooltip target="tooltipReferrer" triggers="hover" placement="right">
                {{ tooltipText }}
              </b-tooltip>
              <textarea
                id="notificationReferrer"
                v-model="form.notificationMessageTemplateReferrerOnRewardIssued"
                class="form-control"
                rows="3"
                :class="{ 'input--error': isSubmit && error.errorNotificationMessageTemplateReferrerOnRewardIssued }"
              ></textarea>
              <div v-if="isSubmit && error.errorNotificationMessageTemplateReferrerOnRewardIssued" class="text-error">
                {{ error.errorNotificationMessageTemplateReferrerOnRewardIssued.message }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-end mt-3">
        <button class="btn btn-outline-danger" @click="onCancel">Cancel</button>
        <button class="btn btn-outline-success ml-10" @click="onSubmit">
          {{ id ? 'Save Campaign' : 'Create Campaign' }}
        </button>
      </div>

      <!-- Success/Error Dialog -->
      <v-dialog v-model="showDialog" width="500" persistent>
        <div class="content-dialog">
          <h3>{{ message }}</h3>
          <div v-if="showSuccess" class="row text-center">
            <div class="col-12">
              <button class="btn-back" @click="() => $router.push({ name: 'ReferralCampaign' })">กลับสู่หน้าหลัก</button>
            </div>
          </div>
          <div v-if="!showSuccess" class="row text-center">
            <div class="col">
              <button class="btn-back" @click="onDialogClose">ปิด</button>
            </div>
          </div>
        </div>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import BrandDropdown from "components/Brand/BrandDropdown.vue";
import DatePicker from "components/Datepicker/Datepicker.vue";
import CkEditor from "components/CkEditor/CkEditor.vue";
import NumberInput from "components/NumberInput/NumberInput.vue";
import FileLoading from "components/Loading/FileLoading.vue";
import ReferralCampaignService from "services/api/referralCampaign.service";
import PrivilegeService from "services/api/privilege.service";
import PresignedUploadUrlService from "services/api/presignedUploadUrl.service";
import ErrorFields from "@/views/ReferralCampaign/errorFields";
import moment from "moment";

export default {
  components: { BrandDropdown, DatePicker, CkEditor, NumberInput, FileLoading },
  data() {
    return {
      id: this.$route.query.id,
      isLoading: false,
      isFileLoading: false,
      isSubmit: false,
      isUpdate: false,
      isInitialLoad: false,
      showDialog: false,
      showSuccess: false,
      message: null,
      selectedLang: "EN",
      tooltipText: "Supported parameters: {{campaign-name}}, {{voucher-expiry-date}}, {{accepted-at}}, {{end-date}}, {{voucher-issued-date}}",
      privilegeOptions: [],
      form: {
        campaignNameEn: null,
        campaignNameTh: null,
        campaignBanner: null,
        bannerPreviewUrl: null,
        brandSelected: null,
        startDate: null,
        endDate: null,
        status: false,
        purchaseWindowDays: null,
        minimumNetAmount: null,
        maxSuccessfulRefereesPerCustomer: null,
        referrerRewardPrivilegeId: null,
        refereeRewardPrivilegeId: null,
        campaignDetailEn: null,
        campaignDetailTh: null,
        termsAndConditionsEn: null,
        termsAndConditionsTh: null,
        notificationMessageTemplateRefereeOnAccept: null,
        notificationMessageTemplateReferrerOnRewardIssued: null,
      },
      error: {
        ...ErrorFields,
      },
    };
  },
  async mounted() {
    if (this.id) {
      this.isLoading = true;
      this.isUpdate = true;
      await this.getCampaignDetail();
    }
  },
  methods: {
    async getPrivileges(brandCode) {
      const params = { privilegeType: "REFERRAL_COUPON" };
      if (brandCode) {
        params.brandCode = brandCode;
      }
      await PrivilegeService.getListPrivileges(params)
        .then((res) => {
          const data = res.data;
          this.privilegeOptions = (data.privileges || []).map((p) => {
            return { id: p.id, text: p.name };
          });
        })
        .catch((err) => {
          console.log("Error fetching privileges: ", err);
        });
    },
    resolveSelectedPrivileges() {
      const referrerPrivilegeId = this.form.referrerRewardPrivilegeId?.id;
      const refereePrivilegeId = this.form.refereeRewardPrivilegeId?.id;
      if (referrerPrivilegeId) {
        this.form.referrerRewardPrivilegeId =
          this.privilegeOptions.find((p) => p.id === referrerPrivilegeId) || null;
      }
      if (refereePrivilegeId) {
        this.form.refereeRewardPrivilegeId =
          this.privilegeOptions.find((p) => p.id === refereePrivilegeId) || null;
      }
    },
    async getCampaignDetail() {
      this.showDialog = false;
      this.isSubmit = false;
      this.isInitialLoad = true;
      await ReferralCampaignService.getReferralCampaignDetail(this.id)
        .then(async (res) => {
          const data = res.data.campaign;
          const form = this.form;
          form.campaignNameEn = data.campaignNameEn;
          form.campaignNameTh = data.campaignNameTh;
          form.campaignBanner = data.campaignBanner;
          form.bannerPreviewUrl = data.campaignBannerUrl || null;
          if (data.brandCode) {
            form.brandSelected = data.brandCode;
            await this.getPrivileges(data.brandCode);
          }
          form.startDate = data.startDate
            ? moment.utc(data.startDate).local().format("YYYY-MM-DD")
            : null;
          form.endDate = data.endDate
            ? moment.utc(data.endDate).local().format("YYYY-MM-DD")
            : null;
          form.status = data.status === "ACTIVE";
          form.purchaseWindowDays = data.purchaseWindowDays;
          form.minimumNetAmount = data.minimumNetAmount;
          form.maxSuccessfulRefereesPerCustomer = data.maxSuccessfulRefereesPerCustomer;
          form.referrerRewardPrivilegeId =
            this.privilegeOptions.find((p) => p.id === data.referrerRewardPrivilegeId) || null;
          form.refereeRewardPrivilegeId =
            this.privilegeOptions.find((p) => p.id === data.refereeRewardPrivilegeId) || null;
          form.campaignDetailEn = data.campaignDetailEn;
          form.campaignDetailTh = data.campaignDetailTh;
          form.termsAndConditionsEn = data.termsAndConditionsEn;
          form.termsAndConditionsTh = data.termsAndConditionsTh;
          form.notificationMessageTemplateRefereeOnAccept = data.notificationMessageTemplateRefereeOnAccept;
          form.notificationMessageTemplateReferrerOnRewardIssued = data.notificationMessageTemplateReferrerOnRewardIssued;
          this.isLoading = false;
          this.isInitialLoad = false;
        })
        .catch((err) => {
          this.isLoading = false;
          this.isInitialLoad = false;
          const errorData = err.response?.data;
          if (errorData?.message) {
            this.showDialog = true;
            this.message = errorData.message;
          }
        });
    },
    onSubmit() {
      this.isSubmit = true;
      this.isLoading = true;
      this.resetErrorText();
      const form = this.form;
      const body = {
        campaignNameEn: form.campaignNameEn,
        campaignNameTh: form.campaignNameTh,
        campaignBanner: form.campaignBanner,
        brandCode: form.brandSelected?.id,
        startDate: form.startDate ? moment(form.startDate).format("YYYY-MM-DD") : null,
        endDate: form.endDate ? moment(form.endDate).format("YYYY-MM-DD") : null,
        status: form.status ? "ACTIVE" : "INACTIVE",
        purchaseWindowDays: form.purchaseWindowDays,
        minimumNetAmount: form.minimumNetAmount,
        maxSuccessfulRefereesPerCustomer: form.maxSuccessfulRefereesPerCustomer,
        referrerRewardPrivilegeId: form.referrerRewardPrivilegeId?.id,
        refereeRewardPrivilegeId: form.refereeRewardPrivilegeId?.id,
        campaignDetailEn: form.campaignDetailEn,
        campaignDetailTh: form.campaignDetailTh,
        termsAndConditionsEn: form.termsAndConditionsEn,
        termsAndConditionsTh: form.termsAndConditionsTh,
        notificationMessageTemplateRefereeOnAccept: form.notificationMessageTemplateRefereeOnAccept,
        notificationMessageTemplateReferrerOnRewardIssued: form.notificationMessageTemplateReferrerOnRewardIssued,
      };
      if (this.id) {
        this.updateExistingCampaign(body);
      } else {
        this.createNewCampaign(body);
      }
    },
    async createNewCampaign(body) {
      await ReferralCampaignService.createReferralCampaign(body)
        .then((res) => { this.createOrUpdateSuccess(res); })
        .catch((err) => { this.createOrUpdateFailure(err); });
    },
    async updateExistingCampaign(body) {
      await ReferralCampaignService.updateReferralCampaign(body, this.id)
        .then((res) => { this.createOrUpdateSuccess(res); })
        .catch((err) => { this.createOrUpdateFailure(err); });
    },
    createOrUpdateSuccess(res) {
      this.isLoading = false;
      const data = res.data;
      this.showDialog = true;
      this.showSuccess = true;
      this.message = data.message ? data.message : "Save referral campaign success";
    },
    createOrUpdateFailure(err) {
      this.isLoading = false;
      const errorData = err.response?.data;
      if (err.response?.status === 409) {
        this.showDialog = true;
        this.message = errorData?.message || "Campaign overlap error";
        return;
      }
      if (errorData?.errors?.length > 0) {
        this.setErrorText(errorData.errors);
        this.showDialog = true;
        this.showSuccess = false;
        this.message = errorData?.message || "กรุณาตรวจสอบข้อมูลที่กรอกให้ถูกต้อง";
        return;
      }
      if (errorData?.message) {
        this.showDialog = true;
        this.message = errorData.message;
      }
    },
    uploadBannerImage(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.form.bannerPreviewUrl = URL.createObjectURL(file);
      this.getPresignURL(file);
    },
    async getPresignURL(file) {
      this.isFileLoading = true;
      await PresignedUploadUrlService.getS3SignUrl("referralCampaign", file)
        .then((res) => {
          const data = res.data;
          this.form.campaignBanner = data.key;
          this.uploadToS3(data.presignedUrl, file);
        })
        .catch((err) => {
          this.isFileLoading = false;
          console.log("Error getting presigned URL: ", err);
        });
    },
    async uploadToS3(url, file) {
      await PresignedUploadUrlService.pushProfilePhotoToS3(url, file)
        .then(() => { this.isFileLoading = false; })
        .catch((err) => {
          this.isFileLoading = false;
          console.log("Error uploading to S3: ", err);
        });
    },
    deleteBannerImage() {
      this.form.campaignBanner = null;
      this.form.bannerPreviewUrl = null;
      this.$refs.uploadBannerImage.value = null;
    },
    changeStartDate(val) {
      if (val > this.form.endDate) {
        this.form.endDate = null;
      }
    },
    async setBrand(brand) {
      const prevBrandId = this.form.brandSelected?.id || this.form.brandSelected;
      this.form.brandSelected = brand || "";
      const newBrandId = brand?.id || brand;
      if (this.isInitialLoad) return;
      if (prevBrandId === newBrandId && this.privilegeOptions.length) return;
      this.form.referrerRewardPrivilegeId = null;
      this.form.refereeRewardPrivilegeId = null;
      this.privilegeOptions = [];
      if (newBrandId) {
        await this.getPrivileges(newBrandId);
      }
    },
    async setBrands(brands) {
      for (const brand of brands) {
        const currentBrandId = this.form.brandSelected?.id || this.form.brandSelected;
        if (brand.id === currentBrandId) {
          this.form.brandSelected = brand;
          if (!this.privilegeOptions.length) {
            await this.getPrivileges(brand.id);
            this.resolveSelectedPrivileges();
          }
          break;
        }
      }
    },
    setCampaignDetailEn(content) { this.form.campaignDetailEn = content || ""; },
    setCampaignDetailTh(content) { this.form.campaignDetailTh = content || ""; },
    setTermsAndConditionsEn(content) { this.form.termsAndConditionsEn = content || ""; },
    setTermsAndConditionsTh(content) { this.form.termsAndConditionsTh = content || ""; },
    onDialogClose() {
      this.showDialog = false;
      this.focusFirstError();
    },
    focusFirstError() {
      const fieldOrder = [
        { errorKey: "errorBrandCode",                                         elementId: null,                              isMultiselect: false, isBrand: true,  lang: null },
        { errorKey: "errorStatus",                                            elementId: "checkStatus",                     isMultiselect: false,                 lang: null },
        { errorKey: "errorStartDate",                                         elementId: "startDatePicker",                 isMultiselect: false,                 lang: null },
        { errorKey: "errorEndDate",                                           elementId: "endDatePicker",                   isMultiselect: false,                 lang: null },
        { errorKey: "errorCampaignBanner",                                    elementId: "uploadBannerImage",               isMultiselect: false,                 lang: null },
        { errorKey: "errorCampaignNameEn",                                    elementId: "campaignName",                    isMultiselect: false,                 lang: "EN" },
        { errorKey: "errorCampaignNameTh",                                    elementId: "campaignName",                    isMultiselect: false,                 lang: "TH" },
        { errorKey: "errorCampaignDetailEn",                                  elementId: null,                              isMultiselect: false,                 lang: "EN" },
        { errorKey: "errorCampaignDetailTh",                                  elementId: null,                              isMultiselect: false,                 lang: "TH" },
        { errorKey: "errorTermsAndConditionsEn",                              elementId: null,                              isMultiselect: false,                 lang: "EN" },
        { errorKey: "errorTermsAndConditionsTh",                              elementId: null,                              isMultiselect: false,                 lang: "TH" },
        { errorKey: "errorPurchaseWindowDays",                                elementId: "purchaseWindowDays",              isMultiselect: false,                 lang: null },
        { errorKey: "errorMinimumNetAmount",                                  elementId: "minimumNetAmount",                isMultiselect: false,                 lang: null },
        { errorKey: "errorMaxSuccessfulRefereesPerCustomer",                  elementId: "maxSuccessfulRefereesPerCustomer", isMultiselect: false,                lang: null },
        { errorKey: "errorReferrerRewardPrivilegeId",                         elementId: "referrerPrivilege",               isMultiselect: true,                  lang: null },
        { errorKey: "errorRefereeRewardPrivilegeId",                          elementId: "refereePrivilege",                isMultiselect: true,                  lang: null },
        { errorKey: "errorNotificationMessageTemplateRefereeOnAccept",        elementId: "notificationReferee",             isMultiselect: false,                 lang: null },
        { errorKey: "errorNotificationMessageTemplateReferrerOnRewardIssued", elementId: "notificationReferrer",            isMultiselect: false,                 lang: null },
      ];

      const firstError = fieldOrder.find((f) => this.error[f.errorKey]);
      if (!firstError) return;

      if (firstError.lang && this.selectedLang !== firstError.lang) {
        this.selectedLang = firstError.lang;
      }

      this.$nextTick(() => {
        if (firstError.isBrand) {
          const brandEl = this.$el.querySelector(".brand-dropdown input, [id*='brand'] input, select");
          if (brandEl) brandEl.focus();
          return;
        }

        if (!firstError.elementId) return;

        if (firstError.isMultiselect) {
          const wrapper = this.$el.querySelector(`#${firstError.elementId}`);
          const input = wrapper?.querySelector("input");
          if (input) input.focus();
          return;
        }

        const el = document.getElementById(firstError.elementId);
        if (el) {
          el.focus();
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    setErrorText(errors) {
      this.error.errorCampaignNameEn = errors.find((e) => e.field === "campaignNameEn");
      this.error.errorCampaignNameTh = errors.find((e) => e.field === "campaignNameTh");
      this.error.errorCampaignBanner = errors.find((e) => e.field === "campaignBanner");
      this.error.errorBrandCode = errors.find((e) => e.field === "brandCode");
      this.error.errorStartDate = errors.find((e) => e.field === "startDate");
      this.error.errorEndDate = errors.find((e) => e.field === "endDate");
      this.error.errorStatus = errors.find((e) => e.field === "status");
      this.error.errorPurchaseWindowDays = errors.find((e) => e.field === "purchaseWindowDays");
      this.error.errorMinimumNetAmount = errors.find((e) => e.field === "minimumNetAmount");
      this.error.errorMaxSuccessfulRefereesPerCustomer = errors.find((e) => e.field === "maxSuccessfulRefereesPerCustomer");
      this.error.errorReferrerRewardPrivilegeId = errors.find((e) => e.field === "referrerRewardPrivilegeId");
      this.error.errorRefereeRewardPrivilegeId = errors.find((e) => e.field === "refereeRewardPrivilegeId");
      this.error.errorCampaignDetailEn = errors.find((e) => e.field === "campaignDetailEn");
      this.error.errorCampaignDetailTh = errors.find((e) => e.field === "campaignDetailTh");
      this.error.errorTermsAndConditionsEn = errors.find((e) => e.field === "termsAndConditionsEn");
      this.error.errorTermsAndConditionsTh = errors.find((e) => e.field === "termsAndConditionsTh");
      this.error.errorNotificationMessageTemplateRefereeOnAccept = errors.find((e) => e.field === "notificationMessageTemplateRefereeOnAccept");
      this.error.errorNotificationMessageTemplateReferrerOnRewardIssued = errors.find((e) => e.field === "notificationMessageTemplateReferrerOnRewardIssued");
    },
    resetErrorText() {
      this.error = { ...ErrorFields };
    },
    onCancel() {
      this.$router.push({ name: "ReferralCampaign" });
    },
  },
};
</script>

<style lang="scss" scoped>
.centered {
  position: absolute;
  top: 45%;
  left: 50%;
}

label[required]::after {
  content: "*";
  color: red;
}

.banner-preview {
  max-width: 100%;
  max-height: 200px;
  object-fit: contain;
}

.btn-clear {
  height: 100%;
  margin-top: 1px;
  white-space: nowrap;
}

.information-title-wrapper {
  background-color: #2a2a2e;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.information-title {
  font-weight: 600;
  font-size: 17px;
  padding: 14px;
  color: #dcdcdc;
}

.information-group {
  border-radius: 8px;
  box-shadow: rgba(0, 0, 0, 0.25) 0 3px 8px, rgba(0, 0, 0, 0.22) 0 2px 2px;
  background-color: #fff;
}

.lang-toggle {
  display: inline-flex;
  border: 1px solid #555;
  border-radius: 4px;
  overflow: hidden;
}

.lang-toggle__btn {
  padding: 4px 16px;
  border: none;
  background: transparent;
  color: #dcdcdc;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;

  &--active {
    background: #0056b3;
    color: #fff;
  }

  &:not(:last-child) {
    border-right: 1px solid #555;
  }

  &:hover:not(.lang-toggle__btn--active) {
    background: rgba(255, 255, 255, 0.1);
  }
}
</style>
